/**
 * Universal search (search.v1) — client for GET /api/v1/admin/search/v1.
 * Contract: docs/search/*, openapi/pocket-api.yaml (SearchV1Envelope).
 * `entity` and `match.kind` are growable vocabularies: keep them open strings.
 */
import { adminFetch } from '@/lib/adminApi';
import type { UnderstandV1 } from '@/lib/search/understandMerge';

export interface SearchV1Result {
  entity: string;
  id: string;
  title: string;
  subtitle: string | null;
  badges: string[];
  owner_app: string;
  url: string | null;
  actionable: boolean;
  /** null on a filter-only listing (empty q with filters). */
  score: number | null;
  match: { field?: string | null; kind?: string };
  updated_at: string | null;
}

export interface SearchV1Group {
  entity: string;
  has_more: boolean;
  results: SearchV1Result[];
}

export interface SearchV1Envelope {
  schema_version: string;
  query: string;
  groups: SearchV1Group[];
  scope_version: string;
  generated_at: string;
  /**
   * Present when Sales was asked for (includeSales) and the caller is staff:
   * `unavailable` means Sales could not be searched, never "no Sales results".
   */
  federation?: { sales?: 'ok' | 'skipped' | 'unavailable' };
}

/**
 * `filters` are the contextual-query params (T-148, R2) as ordered pairs, e.g.
 * from searchFilterChips.toSearchParams; repeatable keys (company_id, model,
 * status) appear once per value. `includeSales` asks the server to search
 * Sales too, as the caller (T-160); the server does so only for staff.
 * `salesOnly` asks for Sales' results alone (ISS-135): the header requests
 * App's and Sales' results in parallel so App's show without waiting on Sales.
 */
export async function searchV1(
  q: string,
  opts: {
    signal?: AbortSignal;
    limit?: number;
    filters?: readonly (readonly [string, string])[];
    includeSales?: boolean;
    salesOnly?: boolean;
  } = {},
): Promise<SearchV1Envelope> {
  const params = new URLSearchParams({ q });
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.salesOnly) params.set('include', 'sales_only');
  else if (opts.includeSales) params.set('include', 'sales');
  for (const [k, v] of opts.filters ?? []) params.append(k, v);
  return adminFetch<SearchV1Envelope>(`/search/v1?${params.toString()}`, { signal: opts.signal });
}

/**
 * AI search understanding (understand.v1, T-151): the filters an AI reads from
 * `q`, or null meaning "use your parser". Anything that isn't a well-formed
 * understand.v1 AI answer is null too, never an error the caller must handle:
 * understanding only ever adds to what the parser already shows.
 */
export async function understandSearch(
  q: string,
  opts: { tz: string; today: string; signal?: AbortSignal },
): Promise<UnderstandV1 | null> {
  let body: unknown;
  try {
    body = await adminFetch<unknown>('/search/understand', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ q, tz: opts.tz, today: opts.today }),
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    return null;
  }
  const b = body as Partial<UnderstandV1> | null;
  const ok = !!b && typeof b === 'object' && b.schema_version === 'understand.v1' && b.source === 'ai'
    && typeof b.q === 'string' && !!b.filters && typeof b.filters === 'object'
    && !!b.labels && typeof b.labels === 'object' && !!b.labels.companies && typeof b.labels.companies === 'object';
  return ok ? (b as UnderstandV1) : null;
}

/** A fleet model as the query parser sees it (docs/search/query-grammar.md §3.3, §3.5). */
export interface SearchModel { code: string; label?: string; callsigns: string[]; rooms?: number }

let modelsPromise: Promise<SearchModel[]> | null = null;

/**
 * The model vocabulary for parsing queries, from the public search aliases
 * (one group per model: code, callsigns, label). Room counts come from the
 * code: `NRE` is the N-room talent trailer. Loaded once per session; a failure
 * is not cached, so the next open retries.
 */
export function getSearchModels(): Promise<SearchModel[]> {
  modelsPromise ??= fetch('/api/v1/public/search/aliases')
    .then((res) => {
      if (!res.ok) throw new Error(`aliases ${res.status}`);
      return res.json() as Promise<{ aliases?: unknown }>;
    })
    .then((body) => (Array.isArray(body.aliases) ? body.aliases : [])
      .filter((g): g is string[] => Array.isArray(g) && typeof g[0] === 'string')
      .map((g) => {
        const rooms = /^([1-5])RE$/.exec(g[0]);
        return {
          code: g[0],
          label: g.length > 1 ? g[g.length - 1] : undefined,
          callsigns: g.slice(1),
          ...(rooms ? { rooms: Number(rooms[1]) } : {}),
        };
      }))
    .catch((err: unknown) => {
      modelsPromise = null;
      throw err;
    });
  return modelsPromise;
}

/** Test seam: forget the cached model list. */
export function _resetSearchModels() {
  modelsPromise = null;
}

/** Human labels for App's and Sales' search entities; unknown ones fall back to the raw name. */
export const ENTITY_LABEL: Record<string, string> = {
  unit: 'Units',
  group: 'Productions',
  site: 'Sites',
  company: 'Companies',
  work_order: 'Work orders',
  damage_finding: 'Damage',
  checkin: 'Check-ins',
  document: 'Documents',
  contact: 'Contacts',
  quote: 'Quotes',
  rental: 'Rentals',
  lead: 'Leads',
  purchase_order: 'Purchase orders',
  vendor: 'Vendors',
  vendor_contact: 'Vendor contacts',
};
